import { useMemo, useState } from "react";
import { Icon } from "../components/Icon";
import { Notice, useNotice } from "../components/Notice";
import { FilterBar, PageHeader, SearchField, SelectField, plural } from "../components/PageHeader";
import { Chip, EmptyState, ErrorState, Skeleton } from "../components/States";
import { fetchQuotePdf, generateQuotePdf, listQuoteInvoices, listQuotes, sendQuoteToXero, xeroInvoiceUrl, type QuoteInvoice } from "../lib/data";
import { aud, daysUntil, fmtDate, fmtPlainDate, humanise } from "../lib/format";
import { useAsync } from "../lib/useAsync";
import { BRAND } from "../lib/config";
import { hashParams, useHash } from "../lib/route";
import { QuoteEditor } from "./QuoteEditor";
import { QuoteView } from "./QuoteView";

const STATUSES = ["draft", "sent", "accepted", "expired"];
const noInvoices = async (): Promise<Record<string, QuoteInvoice>> => ({});

/** #/quotes, #/quotes/new, #/quotes/<id> and #/quotes/<id>/edit. */
export function Quotes() {
  const [id, sub] = hashParams(useHash());
  if (id === "new") return <QuoteEditor />;
  if (id) return <QuoteView key={`${id}/${sub ?? ""}`} id={id} edit={sub === "edit"} />;
  return <QuoteList />;
}

function QuoteList() {
  const { data, error, loading, reload } = useAsync(listQuotes);
  // Xero invoices already raised, by quote id. A failure here only hides the invoice links.
  const invoices = useAsync(BRAND.xero ? listQuoteInvoices : noInvoices);
  const { notice, setNotice, fail, ok } = useNotice();
  const [busy, setBusy] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data ?? []).filter((x) => (!status || x.status === status) && (!needle || [x.quote_number, x.job_title, x.client_name].some((v) => (v ?? "").toLowerCase().includes(needle))));
  }, [data, q, status]);
  const openValue = useMemo(() => (data ?? []).filter((x) => x.status === "sent").reduce((n, x) => n + Number(x.total), 0), [data]);

  /**
   * Open the tab straight away (while the click still counts as a user action, so pop-up
   * blockers allow it), show a waiting message in it, and swap in the PDF when it arrives.
   * If the tab could not be opened, the PDF downloads instead.
   */
  async function withPdf(id: string, number: string, mode: "generate" | "open") {
    setBusy(`${mode}:${id}`);
    setNotice(null);
    const tab = window.open("", "_blank");
    if (tab) {
      tab.document.title = `Quote ${number}`;
      tab.document.body.style.cssText = "font:16px system-ui,sans-serif;padding:32px;color:#333";
      tab.document.body.textContent = mode === "generate" ? `Generating the PDF for quote ${number}. This can take up to a minute.` : `Loading quote ${number}.`;
    }
    try {
      const blob = mode === "generate" ? await generateQuotePdf(id) : await fetchQuotePdf(id);
      const url = URL.createObjectURL(blob);
      if (tab && !tab.closed) tab.location.href = url;
      else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `Quote-${number}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      if (mode === "generate") {
        ok(`PDF ready for ${number}.`);
        reload();
      }
    } catch (e) {
      tab?.close();
      fail(e);
    } finally {
      setBusy(null);
    }
  }

  async function toXero(id: string, number: string) {
    setBusy(`xero:${id}`);
    setNotice(null);
    try {
      const r = await sendQuoteToXero(id);
      const label = r.invoice.remoteNumber ?? "the invoice";
      ok(r.existing ? `${number} is already in Xero as ${label}.` : `${number} is in Xero as draft ${label}. Review and send it from Xero.${r.warnings.length ? ` ${r.warnings.join(" ")}` : ""}`);
      invoices.reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="page" aria-labelledby="page-title">
      <PageHeader
        eyebrow="Sales"
        title="Quotes"
        meta={<>{BRAND.agreements ? "Build a quote, then send it for signing. Accepted quotes are signed online with your terms." : "Quotes for your clients."}{data && openValue > 0 && <> Sent and awaiting reply: <strong>{aud(openValue)}</strong> inc GST.</>}</>}
        actions={<a className="btn btn--primary" href="#/quotes/new"><Icon name="plus" size={18} /> New quote</a>}
      />
      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      <FilterBar count={data ? `Showing ${plural(rows.length, "quote")}` : undefined}>
        <SearchField label="Search quotes" placeholder="Search number, job or client" value={q} onChange={setQ} />
        <SelectField label="Status" value={status} onChange={setStatus} options={[{ value: "", label: "All statuses" }, ...STATUSES.map((s) => ({ value: s, label: humanise(s) }))]} />
      </FilterBar>

      {loading && !data && <Skeleton columns={6} />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && data.length === 0 && <EmptyState help icon="document" title="No quotes yet">Use New quote to build one.</EmptyState>}
      {data && data.length > 0 && rows.length === 0 && <EmptyState icon="search" title="No matching quotes">Try a different number or status.</EmptyState>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table table--stack">
            <thead>
              <tr><th scope="col">Number</th><th scope="col">Created</th><th scope="col">Valid until</th><th scope="col" className="n">Total inc GST</th><th scope="col">Status</th><th scope="col"><span className="sr-only">Actions</span></th></tr>
            </thead>
            <tbody>
              {rows.map((x) => {
                const left = x.valid_until ? daysUntil(x.valid_until) : null;
                return (
                  <tr key={x.id}>
                    <td data-label="Number"><a className="strong mono" href={`#/quotes/${x.id}`}>{x.quote_number}</a>{(x.job_title || x.client_name) && <span className="sub">{[x.job_title, x.client_name].filter(Boolean).join(" · ")}</span>}</td>
                    <td data-label="Created" className="nowrap">{fmtDate(x.created_at)}</td>
                    <td data-label="Valid until" className="nowrap">
                      {x.valid_until ? fmtPlainDate(x.valid_until) : <span className="muted">Not set</span>}
                      {x.status === "sent" && left !== null && left >= 0 && left <= 7 && <span className="sub sub--primary">{left === 0 ? "Expires today" : `${plural(left, "day")} left`}</span>}
                    </td>
                    <td data-label="Total" className="n strong">{aud(Number(x.total))}</td>
                    <td data-label="Status">{BRAND.agreements && x.status === "sent" ? <Chip tone="accent">Awaiting signature</Chip> : BRAND.agreements && x.status === "accepted" ? <Chip tone="green">Signed</Chip> : <Chip value={x.status} />}</td>
                    <td data-label="Actions" className="actions actions--stack">
                      {BRAND.xero && invoices.data?.[x.id] && (
                        <a className="btn btn--outline btn--xs" href={xeroInvoiceUrl(invoices.data[x.id]!.remote_id)} target="_blank" rel="noreferrer">
                          <Icon name="external" size={16} /> {invoices.data[x.id]!.remote_number ?? "Xero invoice"}
                          <span className="sr-only"> in Xero for {x.quote_number}</span>
                        </a>
                      )}
                      {BRAND.xero && x.status === "accepted" && invoices.data && !invoices.data[x.id] && (
                        <button type="button" className="btn btn--outline btn--xs" disabled={busy !== null} aria-busy={busy === `xero:${x.id}`} onClick={() => toXero(x.id, x.quote_number)}>
                          <Icon name="arrow" size={16} /> {busy === `xero:${x.id}` ? "Sending" : "Send to Xero"}
                          <span className="sr-only"> {x.quote_number}</span>
                        </button>
                      )}
                      {BRAND.quotePdf && x.pdf_path && (
                        <button type="button" className="btn btn--outline btn--xs" disabled={busy !== null} onClick={() => withPdf(x.id, x.quote_number, "open")}>
                          <Icon name="file" size={16} /> {busy === `open:${x.id}` ? "Opening" : "Open PDF"}
                          <span className="sr-only"> for {x.quote_number}</span>
                        </button>
                      )}
                      {BRAND.quotePdf && (
                      <button type="button" className="btn btn--outline btn--xs" disabled={busy !== null} aria-busy={busy === `generate:${x.id}`} onClick={() => withPdf(x.id, x.quote_number, "generate")}>
                        <Icon name="file" size={16} /> {busy === `generate:${x.id}` ? "Generating" : x.pdf_path ? "Regenerate" : "Create PDF"}
                        <span className="sr-only"> for {x.quote_number}</span>
                      </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
